import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import CameraButton from '../UISystem/Chat/CameraButton';
import { COLORS, SHADOWS } from '../../styles/UISystem';
import { getCurrentLocation, reverseGeocode } from '../../services/location';

/**
 * PostComposerSheet — the "New Post" bottom sheet
 * (design: 2026-07-13_posts/posts-new-overly.png, minus the Share-with row —
 * Phase 1 is broadcast-only; audience selection is its own fast-follow branch).
 *
 * Standard user-initiated sheet (§4.2): scrim + backdrop dismiss, 24px top
 * corners, slide-up 0.3s easeOut, body scroll lock. Modeled on
 * ShareLocationSheet's Framer-Motion pattern.
 *
 * Behaviors (decided at /refine, 2026-07-13):
 * - Photo uploads at Done, not at selection — removing/replacing the attached
 *   photo before posting is pure local state, no orphaned uploads.
 * - Location tag is tap-to-toggle for THIS post only; every new sheet starts
 *   with location ON. Capture starts on sheet open, silently (no permission
 *   prompt from a composer); the post ships with whatever has resolved by Done.
 * - Done disabled until there's content (text or photo).
 *
 * @param {function} onPost - async ({ text, photoFile, locationSnapshot }) =>
 *   creates the post; the sheet shows inline errors and stays open on failure
 * @param {function} onClose - backdrop tap / successful post
 */
const PostComposerSheet = ({ onPost, onClose }) => {
  const [text, setText] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [neighborhood, setNeighborhood] = useState('');
  const [locationOn, setLocationOn] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);

  const textareaRef = useRef(null);
  const replaceInputRef = useRef(null);

  // Body scroll lock (§4.7 — match ShareLocationSheet)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Location capture starts on sheet open — and PROMPTS if permission hasn't
  // been decided yet (user-decided 2026-07-13 device review: tapping + should
  // ask for location when it isn't already allowed). A denial just means no
  // tag; location never blocks posting.
  useEffect(() => {
    let cancelled = false;

    const capture = async () => {
      try {
        const coords = await getCurrentLocation();
        const geo = await reverseGeocode(coords.latitude, coords.longitude);
        // Bare neighborhood, fallback city ("Lower East Side", not
        // "Lower East Side, New York") — post-tag format, not hometown format.
        const label = geo?.address?.neighborhood || geo?.address?.city || '';
        if (label && !cancelled) setNeighborhood(label);
      } catch (e) {
        // Best-effort — the post just ships without a location tag.
      }
    };

    capture();
    return () => {
      cancelled = true;
    };
  }, []);

  // PWA keyboard lift: fixed-bottom sheets don't move with the iOS keyboard —
  // the keyboard just covers them (device-verified 2026-07-13). Lift the sheet
  // by the visual-viewport overlap so the input stays visible while typing.
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onViewportChange = () => {
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardOffset(overlap);
    };
    vv.addEventListener('resize', onViewportChange);
    vv.addEventListener('scroll', onViewportChange);
    return () => {
      vv.removeEventListener('resize', onViewportChange);
      vv.removeEventListener('scroll', onViewportChange);
    };
  }, []);

  // Local-only photo preview; object URL revoked on change/unmount
  useEffect(() => {
    if (!photoFile) {
      setPhotoPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [photoFile]);

  // Auto-expanding textarea (ChatInputNormal pattern: grow, then scroll)
  const handleTextChange = (e) => {
    setText(e.target.value);
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
    }
  };

  const hasContent = !!text.trim() || !!photoFile;

  const handleDone = async () => {
    if (!hasContent || posting) return;
    setPosting(true);
    setError(null);
    try {
      await onPost({
        text,
        photoFile,
        locationSnapshot: locationOn && neighborhood ? neighborhood : null,
      });
      onClose();
    } catch (err) {
      console.error('[COMPOSER] Post failed:', err?.message);
      setError("Couldn't post — try again");
      setPosting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <motion.div
        initial={{ y: 500 }}
        animate={{ y: 0 }}
        exit={{ y: 500 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxHeight: '85dvh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
          // Ride above the iOS keyboard (0 when closed)
          marginBottom: keyboardOffset ? `${keyboardOffset}px` : 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '12px 20px 28px',
            // Safe-area padding is a dead gap while the keyboard covers the
            // home indicator (ConnectionPageShared's PWA idiom)
            paddingBottom: keyboardOffset ? '20px' : 'calc(28px + env(safe-area-inset-bottom))',
          }}
        >
          {/* Drag handle */}
          <div
            style={{
              width: '36px',
              height: '4px',
              borderRadius: '2px',
              backgroundColor: COLORS.MEDIUM_DARK_GRAY,
              alignSelf: 'center',
            }}
          />

          {/* Title */}
          <div
            style={{
              fontSize: '17px',
              fontWeight: 700,
              color: '#000000',
              textAlign: 'center',
              margin: '14px 0 16px',
            }}
          >
            New Post
          </div>

          {/* Input container: textarea + (thumbnail) + camera/location row */}
          <div
            style={{
              border: `1px solid ${COLORS.MEDIUM_DARK_GRAY}`,
              borderRadius: '16px',
              padding: '14px 14px 10px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              placeholder="What's happening?"
              rows={3}
              style={{
                border: 'none',
                outline: 'none',
                resize: 'none',
                width: '100%',
                minHeight: '72px',
                maxHeight: '150px',
                fontSize: '17px',
                lineHeight: 1.4,
                fontFamily: 'inherit',
                color: '#000000',
                backgroundColor: 'transparent',
              }}
            />

            {/* Attached photo: thumbnail preview; × removes, tap replaces */}
            {photoPreviewUrl && (
              <div style={{ position: 'relative', alignSelf: 'flex-start', marginBottom: '8px' }}>
                <img
                  src={photoPreviewUrl}
                  alt=""
                  onClick={() => replaceInputRef.current?.click()}
                  style={{
                    width: '72px',
                    height: '72px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    display: 'block',
                    cursor: 'pointer',
                  }}
                />
                <div
                  onClick={() => setPhotoFile(null)}
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <X size={12} color="#FFFFFF" weight="bold" />
                </div>
                {/* Hidden input for tap-to-replace (CameraButton owns its own) */}
                <input
                  ref={replaceInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setPhotoFile(file);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {/* Bottom row: camera left, location tag right */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <CameraButton onTakePhoto={(file) => setPhotoFile(file)} />
              {neighborhood && (
                <div
                  onClick={() => setLocationOn((on) => !on)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    padding: '4px 0',
                  }}
                >
                  {/* Pin rule (§5.2): filled green = live location on; gray outline = off */}
                  <MapPin
                    size={16}
                    weight={locationOn ? 'fill' : 'regular'}
                    color={locationOn ? '#4CAF50' : '#8E8E93'}
                  />
                  <span
                    style={{
                      fontSize: '14px',
                      color: locationOn ? '#2E7D32' : '#8E8E93',
                      textDecoration: locationOn ? 'none' : 'line-through',
                    }}
                  >
                    {neighborhood}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Inline error — post failed; sheet stays open to retry */}
          {error && (
            <div
              style={{
                fontSize: '14px',
                color: '#D92D20',
                textAlign: 'center',
                marginTop: '12px',
              }}
            >
              {error}
            </div>
          )}

          {/* Done */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
            <ButtonRounded
              text={posting ? 'Posting…' : 'Done'}
              onClick={handleDone}
              size="large"
              variant="primary"
              disabled={!hasContent || posting}
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PostComposerSheet;
